import { Component, TableColumn } from '@/types';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useViewerStore } from '@/stores/viewer/viewer-store';

export interface ColumnFieldDefinition {
  name: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'hidden';
  required: boolean;
  options?: Array<{ value: string; label: string; color?: string }>;
  defaultValue?: unknown;
}

/**
 * Infer field type based on column properties
 */
function inferFieldType(accessorKey: string, columnId: string): 'text' | 'select' | 'date' | 'hidden' {
  const key = accessorKey.toLowerCase();
  const id = columnId.toLowerCase();

  // Hidden fields (e.g., actions column)
  if (key.includes('action') || id.includes('action')) {
    return 'hidden';
  }

  // Date fields
  if (key.includes('date') || key.includes('maintenance') || key.includes('created') || key.includes('updated')) {
    return 'date';
  }

  // Select fields
  if (key.includes('status') || key.includes('type') || key.includes('category') || key.includes('priority')) {
    return 'select';
  }

  // Default to text
  return 'text';
}

/**
 * Get predefined options for select fields
 * Extracts options from existing table data to make it truly dynamic
 */
function getFieldOptions(
  accessorKey: string,
  tableData?: Record<string, unknown>[]
): Array<{ value: string; label: string; color?: string }> | undefined {
  const key = accessorKey.toLowerCase();

  // For status fields, use predefined options with colors
  if (key.includes('status')) {
    return [
      { value: 'Active', label: 'Active', color: '#10b981' },
      { value: 'Inactive', label: 'Inactive', color: '#6B7280' },
      { value: 'Maintenance', label: 'Maintenance', color: '#f59e0b' },
    ];
  }

  // For other select fields, extract unique values from existing table data
  if (tableData && tableData.length > 0) {
    const uniqueValues = new Set<string>();
    const colorMap = new Map<string, string>();

    tableData.forEach((row) => {
      const value = row[accessorKey];
      if (value && typeof value === 'string') {
        uniqueValues.add(value);

        // If this field has an associated color field (like statusColor), capture it
        const colorKey = `${accessorKey}Color`;
        const color = row[colorKey];
        if (color && typeof color === 'string') {
          colorMap.set(value, color);
        }
      }
    });

    if (uniqueValues.size > 0) {
      return Array.from(uniqueValues).sort().map((value) => ({
        value,
        label: value,
        color: colorMap.get(value),
      }));
    }
  }

  return undefined;
}

/**
 * Extract column definitions from a table component
 * Dynamically reads column names, types, and options from the table
 */
export function extractColumnDefinitions(tableComponent: Component): ColumnFieldDefinition[] {
  const columns = (tableComponent.properties.columns as TableColumn[]) || [];
  const tableData = (tableComponent.properties.data as Record<string, unknown>[]) || [];

  return columns
    .filter((col) => {
      // Skip action columns
      const key = col.accessorKey.toLowerCase();
      return !key.includes('action');
    })
    .map((col) => {
      const fieldType = inferFieldType(col.accessorKey, col.id);
      const isRequired = col.id.includes('name') || col.id.includes('type');

      return {
        name: col.accessorKey,
        label: col.header,
        type: fieldType,
        required: isRequired,
        options: fieldType === 'select' ? getFieldOptions(col.accessorKey, tableData) : undefined,
        defaultValue: fieldType === 'hidden' ? 'edit,delete' : undefined,
      };
    });
}

/**
 * Generate a unique ID for a new table row
 */
export function generateNewRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Validate row data against column definitions
 */
export function validateRowData(
  rowData: Record<string, unknown>,
  columns: ColumnFieldDefinition[]
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  columns.forEach((col) => {
    if (col.required && !rowData[col.name]) {
      errors.push(`${col.label} is required`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Add a new row to a table component
 */
export function addRowToTable(tableId: string, rowData: Record<string, unknown>): boolean {
  try {
    // Try editor store first
    const editorState = useEditorStore.getState();
    let tableComponent = editorState.getComponent?.(tableId);
    let updateComponent = editorState.updateComponent;

    // If not found in editor, try viewer store
    if (!tableComponent) {
      const viewerState = useViewerStore.getState();
      tableComponent = viewerState.getComponentById?.(tableId);
      updateComponent = viewerState.updateComponent;
    }

    if (!tableComponent) {
      console.error(`Table component with id "${tableId}" not found in either store`);
      return false;
    }

    // Generate new row with unique ID
    const newRow = {
      id: generateNewRowId(),
      ...rowData,
    };

    // Get current data array
    const currentData = Array.isArray(tableComponent.properties.data)
      ? (tableComponent.properties.data as Record<string, unknown>[])
      : [];

    // Update table component with new row
    updateComponent(tableId, {
      properties: {
        ...tableComponent.properties,
        data: [...currentData, newRow],
      },
    });

    return true;
  } catch (error) {
    console.error('Error adding row to table:', error);
    return false;
  }
}

/**
 * Update an existing row in a table component
 */
export function updateTableRow(
  tableId: string,
  rowId: string,
  rowData: Record<string, unknown>
): boolean {
  try {
    // Try editor store first
    const editorState = useEditorStore.getState();
    let tableComponent = editorState.getComponent?.(tableId);
    let updateComponent = editorState.updateComponent;

    // If not found in editor, try viewer store
    if (!tableComponent) {
      const viewerState = useViewerStore.getState();
      tableComponent = viewerState.getComponentById?.(tableId);
      updateComponent = viewerState.updateComponent;
    }

    if (!tableComponent) {
      console.error(`Table component with id "${tableId}" not found in either store`);
      return false;
    }

    // Get current data array
    const currentData = Array.isArray(tableComponent.properties.data)
      ? (tableComponent.properties.data as Record<string, unknown>[])
      : [];

    // Find and update the row
    const updatedData = currentData.map((row) => {
      if ((row as { id: string }).id === rowId) {
        return { ...row, ...rowData };
      }
      return row;
    });

    // Update table component
    updateComponent(tableId, {
      properties: {
        ...tableComponent.properties,
        data: updatedData,
      },
    });

    return true;
  } catch (error) {
    console.error('Error updating table row:', error);
    return false;
  }
}

/**
 * Delete a row from a table component
 */
export function deleteTableRow(tableId: string, rowId: string): boolean {
  try {
    // Try editor store first
    const editorState = useEditorStore.getState();
    let tableComponent = editorState.getComponent?.(tableId);
    let updateComponent = editorState.updateComponent;

    // If not found in editor, try viewer store
    if (!tableComponent) {
      const viewerState = useViewerStore.getState();
      tableComponent = viewerState.getComponentById?.(tableId);
      updateComponent = viewerState.updateComponent;
    }

    if (!tableComponent) {
      console.error(`Table component with id "${tableId}" not found in either store`);
      return false;
    }

    // Get current data array
    const currentData = Array.isArray(tableComponent.properties.data)
      ? (tableComponent.properties.data as Record<string, unknown>[])
      : [];

    // Filter out the row
    const updatedData = currentData.filter((row) => (row as { id: string }).id !== rowId);

    // Update table component
    updateComponent(tableId, {
      properties: {
        ...tableComponent.properties,
        data: updatedData,
      },
    });

    return true;
  } catch (error) {
    console.error('Error deleting table row:', error);
    return false;
  }
}
